import { Vendor } from '@models/vendor';

export interface AgingProductInput {
  status?: string;
  lastInvoiceDate?: number | string;
}

export const STATUS_YELLOW = '#ffcc33';
export const STATUS_RED = '#ff3333';

export interface InventoryStatusDisplay {
  cssClass?: string;
  backgroundColor?: string;
}

type AgingStage =
  | { kind: 'base'; status: string }
  | { kind: 'yellow' }
  | { kind: 'gradient'; step: number }
  | { kind: 'red' };

const BASE_STATUS_SORT_RANKS: Record<string, number> = {
  white: 0,
  green: 1,
  yellow: 2,
  red: 3,
  purple: 4,
  black: 5,
};

/** Empty, null, undefined, or whitespace-only status is treated as white (auto aging). */
export function normalizeStatus(status?: string | null): string {
  const trimmed = (status ?? '').trim().toLowerCase();
  return trimmed || 'white';
}

export function usesAgingCalculation(product: AgingProductInput): boolean {
  return normalizeStatus(product.status) === 'white';
}

function hexToRgb(hex: string): { r: number; g: number; b: number } {
  const normalized = hex.replace('#', '');
  return {
    r: parseInt(normalized.substring(0, 2), 16),
    g: parseInt(normalized.substring(2, 4), 16),
    b: parseInt(normalized.substring(4, 6), 16),
  };
}

function interpolateHexColor(from: string, to: string, factor: number): string {
  const start = hexToRgb(from);
  const end = hexToRgb(to);
  const r = Math.round(start.r + (end.r - start.r) * factor);
  const g = Math.round(start.g + (end.g - start.g) * factor);
  const b = Math.round(start.b + (end.b - start.b) * factor);
  return `#${[r, g, b]
    .map((value) => value.toString(16).padStart(2, '0'))
    .join('')}`;
}

export function getAgingGradientColors(steps = 5): string[] {
  return Array.from({ length: steps }, (_, index) =>
    interpolateHexColor(STATUS_YELLOW, STATUS_RED, (index + 1) / steps)
  );
}

const AGING_GRADIENT_COLORS = getAgingGradientColors();

export function startOfDay(date: Date): Date {
  const normalized = new Date(date.getTime());
  normalized.setHours(0, 0, 0, 0);
  return normalized;
}

export function addMonths(date: Date, months: number): Date {
  const result = new Date(date.getTime());
  const day = result.getDate();
  result.setMonth(result.getMonth() + months);
  if (result.getDate() < day) {
    result.setDate(0);
  }
  return result;
}

function resolveAgingStage(
  product: AgingProductInput,
  vendor: Vendor,
  today: Date = new Date()
): AgingStage {
  const baseStatus = normalizeStatus(product.status);
  const aging1 = vendor?.aging1 ?? 9;
  const aging2 = vendor?.aging2 ?? 18;

  if (!product.lastInvoiceDate || aging2 <= aging1) {
    return { kind: 'base', status: baseStatus };
  }

  const invoiceDate = startOfDay(new Date(product.lastInvoiceDate));
  const now = startOfDay(today);
  const aging1Date = startOfDay(addMonths(invoiceDate, aging1));
  const aging2Date = startOfDay(addMonths(invoiceDate, aging2));
  const stepMonths = (aging2 - aging1) / 5;

  if (now < aging1Date) {
    return { kind: 'base', status: baseStatus };
  }

  const firstGradientDate = startOfDay(addMonths(invoiceDate, aging1 + stepMonths));
  if (now < firstGradientDate) {
    return { kind: 'yellow' };
  }

  if (now >= aging2Date) {
    return { kind: 'red' };
  }

  for (let step = 1; step <= 4; step++) {
    const stepStart = startOfDay(addMonths(invoiceDate, aging1 + step * stepMonths));
    const stepEnd = startOfDay(addMonths(invoiceDate, aging1 + (step + 1) * stepMonths));
    if (now >= stepStart && now < stepEnd) {
      return { kind: 'gradient', step };
    }
  }

  return { kind: 'base', status: baseStatus };
}

function mapAgingStageToSelectValue(stage: AgingStage): string {
  switch (stage.kind) {
    case 'base':
      return stage.status;
    case 'yellow':
      return 'yellow';
    case 'gradient':
      return stage.step >= 3 ? 'red' : 'yellow';
    case 'red':
      return 'red';
  }
}

export function getEffectiveStatusForSelect(
  product: AgingProductInput,
  vendor: Vendor,
  today: Date = new Date()
): string {
  if (!usesAgingCalculation(product)) {
    return normalizeStatus(product.status);
  }
  return mapAgingStageToSelectValue(resolveAgingStage(product, vendor, today));
}

export function getInventoryStatusSortRank(
  product: AgingProductInput,
  vendor: Vendor,
  today: Date = new Date()
): number {
  if (!usesAgingCalculation(product)) {
    return BASE_STATUS_SORT_RANKS[normalizeStatus(product.status)] ?? 6;
  }

  const stage = resolveAgingStage(product, vendor, today);

  switch (stage.kind) {
    case 'base':
      return BASE_STATUS_SORT_RANKS[stage.status] ?? 6;
    case 'yellow':
      return 10;
    case 'gradient':
      return 10 + stage.step;
    case 'red':
      return 15;
  }
}

export function getInventoryStatusDisplay(
  product: AgingProductInput,
  vendor: Vendor,
  today: Date = new Date()
): InventoryStatusDisplay {
  if (!usesAgingCalculation(product)) {
    return { cssClass: normalizeStatus(product.status) };
  }

  const stage = resolveAgingStage(product, vendor, today);

  switch (stage.kind) {
    case 'base':
      return { cssClass: stage.status };
    case 'yellow':
      return { cssClass: 'yellow' };
    case 'gradient':
      return { backgroundColor: AGING_GRADIENT_COLORS[stage.step - 1] };
    case 'red':
      return { backgroundColor: STATUS_RED };
  }
}
